import React, { createContext, useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import Joyride, { CallBackProps } from 'react-joyride';
import { featureFlags } from 'src/config/featureFlags';
import { TourContextValue } from './types';
import { AppTour } from './tours/AppTour';
import { DEFAULT_SEQUENCE_ID, TOUR_SEQUENCES } from './tourSequences';
import { TourWelcomeModal } from './components/TourWelcomeModal';
import { CustomTooltip } from './components/CustomTooltip';
import { useTourPersistence } from './hooks/useTourPersistence';
import { useTourSequence } from './hooks/useTourSequence';
import { useTourSteps } from './hooks/useTourSteps';
import { useTourUrlParams } from './hooks/useTourUrlParams';
import { useJoyrideCallback } from './hooks/useJoyrideCallback';

export const TourContext = createContext<TourContextValue | undefined>(undefined);

export const TourProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [appTour] = useState<AppTour>(new AppTour());
  const [run, setRun] = useState(false);
  const [tourVersion, setTourVersion] = useState(0);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [joyrideKey, setJoyrideKey] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  // Tracks the live Joyride step index so onClosed knows which step the user was on.
  const currentJoyrideStepRef = React.useRef(0);
  const pausedRef = React.useRef(false);

  const location = useLocation();
  const persistence = useTourPersistence();
  const sequence = useTourSequence(persistence);

  // stepOffset: how many steps to slice from the front of the steps array.
  // Initialised from persistence so a page-refresh resumes from the last closed step.
  // Must be declared after `persistence` to avoid a "cannot access before initialization" error.
  const [stepOffset, setStepOffset] = useState(() => persistence.state.active?.stepIndex ?? 0);

  // On page load/refresh, if a sequence was mid-flight, target that specific tour.
  // This prevents the auto-tour path from picking up a random tour with visible steps
  // on the wrong page (e.g. a sidebar tour showing on /help-center after HMR reload).
  // The targeted tour's steps won't be in the DOM until the user navigates to the
  // correct page, so activeSteps stays empty and the tour doesn't start prematurely.
  const [manualTourId, setManualTourId] = useState<string | null>(() => {
    const { active, hasSkippedAll } = persistence.state;
    if (!hasSkippedAll && active?.sequenceId && active.tourId) {
      return active.tourId;
    }
    return null;
  });

  const { activeSteps, activeTour } = useTourSteps(appTour, persistence.state, manualTourId, tourVersion, location.pathname, stepOffset, isPaused);

  // Subscribe to tour registration updates from components
  useEffect(() => {
    const unsubscribe = appTour.subscribe(() => {
      setTourVersion((v) => v + 1);
    });
    return () => {
      unsubscribe();
    };
  }, [appTour]);

  // Re-evaluate steps when location changes (allow DOM to render)
  useEffect(() => {
    const timer = setTimeout(() => {
      setTourVersion((v) => v + 1);
    }, 500);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  // Resume a tour in a sequence when the user navigates to the correct page.
  // Do NOT call resetTour here — resetTour clears `active` when active.tourId matches,
  // which destroys isInSequence and breaks the sequence flow.
  // advance() already called resetTour + setActive earlier.
  const resumeSequenceTour = useCallback((tourId: string) => {
    pausedRef.current = false;
    setIsPaused(false);
    setStepOffset(0);
    currentJoyrideStepRef.current = 0;
    setManualTourId(tourId);
    setTimeout(() => {
      setRun(true);
    }, 500);
  }, []);

  // Location-based sequence continuation
  useEffect(() => {
    if (sequence.isInSequence && !run && sequence.currentTourId) {
      const { currentTourId } = sequence;

      if (currentTourId === 'audit-dashboard' && location.pathname === '/dashboard') {
        resumeSequenceTour('audit-dashboard');
      }

      if (currentTourId === 'action-details' && location.pathname.startsWith('/action-details/')) {
        resumeSequenceTour('action-details');
      }

      if (currentTourId === 'rate-card-table' && location.pathname.startsWith('/ratecard-manager')) {
        resumeSequenceTour('rate-card-table');
      }

      if (currentTourId === 'rate-card-charges' && location.pathname.startsWith('/ratecard-manager')) {
        resumeSequenceTour('rate-card-charges');
      }
    }
  }, [location.pathname, sequence, run, persistence, resumeSequenceTour]);

  // Start a single tour manually (resets it and plays from beginning)
  const handleStartManualTour = useCallback(
    (tourId: string) => {
      pausedRef.current = false;
      setIsPaused(false);
      setStepOffset(0);
      currentJoyrideStepRef.current = 0;
      setManualTourId(tourId);

      if (persistence.state.hasSkippedAll) {
        persistence.setSkippedAll(false);
      }

      persistence.resetTour(tourId);

      if (!persistence.state.hasSeenWelcome) {
        persistence.setWelcomeSeen();
      }

      persistence.setActive({
        sequenceId: null,
        tourId,
        tourIndex: 0,
        stepIndex: 0,
      });

      setTimeout(() => {
        setRun(true);
      }, 100);
    },
    [persistence]
  );

  // Handle URL params (?tour=X&tourMode=manual)
  useTourUrlParams({
    sequence,
    onStartManualTour: handleStartManualTour,
  });

  // Joyride callback — minimal, all logic here in TourProvider
  const { handleCallback } = useJoyrideCallback({
    onClosed: () => {
      // Preserve position: beacon reappears at the step where the user closed.
      // stepOffset is the slice base; currentJoyrideStepRef.current is Joyride's index
      // within that slice — summing them gives the absolute step position.
      const absoluteOffset = stepOffset + currentJoyrideStepRef.current;
      setStepOffset(absoluteOffset);
      persistence.updateStepIndex(absoluteOffset);
      currentJoyrideStepRef.current = 0;
      // Remount Joyride by changing key — resets internal state to beacon.
      // Keep run=true so Joyride renders, and disableBeacon:false shows the beacon.
      pausedRef.current = true;
      setIsPaused(true);
      setJoyrideKey((k) => k + 1);
    },
    onResumed: () => {
      // Beacon was clicked — allow auto-start again for future tours
      pausedRef.current = false;
    },
    onFinished: () => {
      // manualTourId is current React state here — always fresh
      const finishedId = manualTourId;

      // 1. Mark the finished tour as completed
      if (finishedId) {
        persistence.markTourCompleted(finishedId);
      }

      // 2. Handle sequence: advance to next tour or end
      if (sequence.isInSequence) {
        const nextTourId = sequence.advance();

        if (nextTourId) {
          // Advance to next tour — do NOT force setRun(true) here.
          // If the next tour's steps are visible on this page, auto-start will fire.
          // If the next tour is on a different page, auto-start fires once the user
          // navigates there and the steps become visible in the DOM.
          // Calling setRun(true) here while steps=[](different page) causes Joyride to
          // fire STATUS.FINISHED immediately, prematurely ending the sequence.
          setRun(false);
          setIsPaused(false);
          setStepOffset(0);
          currentJoyrideStepRef.current = 0;
          setManualTourId(nextTourId);
          setTourVersion((v) => v + 1);
          return;
        }
      }

      // 3. No next tour — clean up. Pause to prevent auto-start of unrelated tours.
      pausedRef.current = true;
      persistence.clearCompletedTours();
      persistence.clearActive();
      persistence.setSkippedAll(true);
      setRun(false);
      setManualTourId(null);
      setTourVersion((v) => v + 1);
    },
    onSkipped: () => {
      const skippedId = manualTourId;

      // Mark skipped tour as completed so it won't replay
      if (skippedId) {
        persistence.markTourCompleted(skippedId);
      }

      // In a sequence: advance to next tour (same as onFinished)
      if (sequence.isInSequence) {
        const nextTourId = sequence.advance();

        if (nextTourId) {
          // Same reasoning as onFinished: don't force setRun(true) immediately.
          setRun(false);
          setIsPaused(false);
          setStepOffset(0);
          currentJoyrideStepRef.current = 0;
          setManualTourId(nextTourId);
          setTourVersion((v) => v + 1);
          return;
        }
      }

      // No next tour — clean up. Pause to prevent auto-start of unrelated tours.
      pausedRef.current = true;
      persistence.clearCompletedTours();
      persistence.clearActive();
      persistence.setSkippedAll(true);
      setRun(false);
      setManualTourId(null);
      setTourVersion((v) => v + 1);
    },
  });

  // Wrap Joyride callback to track the live step index.
  // currentJoyrideStepRef.current is read by onClosed to compute the absolute offset.
  const wrappedCallback = useCallback(
    (data: CallBackProps) => {
      currentJoyrideStepRef.current = data.index;
      handleCallback(data);
    },
    [handleCallback]
  );

  // Auto-start logic
  useEffect(() => {
    const { hasSkippedAll, hasSeenWelcome } = persistence.state;

    if (activeSteps.length === 0 || run || !featureFlags.GUIDED_TOURS || hasSkippedAll || pausedRef.current) {
      return undefined;
    }

    // For requireAllSteps tours, wait until all defined steps are in the DOM before auto-starting.
    // Instead of blocking useTourSteps (which would trigger the stop effect), we retry here.
    // Each retry bumps tourVersion → useTourSteps recomputes → steps re-checked.
    if (activeTour?.requireAllSteps) {
      const totalDefined = Object.keys(activeTour.steps).length;
      if (activeSteps.length < totalDefined) {
        const retry = setTimeout(() => {
          setTourVersion((v) => v + 1);
        }, 300);
        return () => clearTimeout(retry);
      }
    }

    if (!hasSeenWelcome) {
      setShowWelcomeModal(true);
      return undefined;
    }

    const timer = setTimeout(() => {
      setRun(true);
    }, 100);
    return () => clearTimeout(timer);
  }, [activeSteps, run, persistence.state, activeTour]);

  // Stop Joyride if no steps available
  useEffect(() => {
    if (run && activeSteps.length === 0) {
      setRun(false);
    }
  }, [run, activeSteps]);

  // Welcome modal handlers
  const handleStartTour = () => {
    setShowWelcomeModal(false);
    pausedRef.current = false;
    setIsPaused(false);
    setStepOffset(0);
    currentJoyrideStepRef.current = 0;
    persistence.setWelcomeSeen();
    persistence.setSkippedAll(false);
    sequence.startSequence(DEFAULT_SEQUENCE_ID);

    // Set manualTourId to the first tour so onFinished can mark it completed
    const firstTourId = TOUR_SEQUENCES[DEFAULT_SEQUENCE_ID]?.tours[0] ?? 'top-widget';
    setManualTourId(firstTourId);

    setTimeout(() => {
      setRun(true);
    }, 100);
  };

  const handleSkipTour = () => {
    setShowWelcomeModal(false);
    persistence.setWelcomeSeen();
    persistence.setSkippedAll(true);

    // Mark current tour as completed
    if (persistence.state.active?.tourId) {
      persistence.markTourCompleted(persistence.state.active.tourId);
    }
    persistence.clearActive();
    setTourVersion((v) => v + 1);
  };

  // Start a sequence from external components (e.g. Help Center)
  const handleStartSequence = useCallback(
    (sequenceId: string) => {
      const seq = TOUR_SEQUENCES[sequenceId];
      if (!seq || seq.tours.length === 0) return;

      pausedRef.current = false;
      setIsPaused(false);
      setStepOffset(0);
      currentJoyrideStepRef.current = 0;
      persistence.setSkippedAll(false);
      sequence.startSequence(sequenceId);
      const firstTourId = seq.tours[0];
      setManualTourId(firstTourId);
      setTourVersion((v) => v + 1);

      setTimeout(() => {
        setRun(true);
      }, 100);
    },
    [sequence]
  );

  const refreshTourSteps = useCallback(() => {
    setTourVersion((v) => v + 1);
  }, []);

  const finishCurrentTour = useCallback(() => {
    const finishedId = manualTourId;

    // Mark the current tour as completed
    if (finishedId) {
      persistence.markTourCompleted(finishedId);
    }

    // Handle sequence: advance to next tour or end
    if (sequence.isInSequence) {
      const nextTourId = sequence.advance();

      if (nextTourId) {
        // Advance to next tour
        setRun(false);
        setIsPaused(false);
        setStepOffset(0);
        currentJoyrideStepRef.current = 0;
        setManualTourId(nextTourId);
        setTourVersion((v) => v + 1);
        // Small delay to allow DOM updates before starting next tour
        setTimeout(() => {
          setRun(true);
        }, 150);
        return;
      }
    }

    // No next tour - clean up
    pausedRef.current = true;
    persistence.clearCompletedTours();
    persistence.clearActive();
    persistence.setSkippedAll(true);
    setRun(false);
    setManualTourId(null);
    setTourVersion((v) => v + 1);
  }, [manualTourId, persistence, sequence]);

  // Context value
  const contextValue = useMemo<TourContextValue>(
    () => ({
      completedTours: persistence.state.completedTours,
      appTour,
      startTour: handleStartManualTour,
      startSequence: handleStartSequence,
      activeSequence: sequence.activeSequence,
      isInSequence: sequence.isInSequence,
      cancelSequence: () => {
        sequence.cancel();
        setRun(false);
        setManualTourId(null);
      },
      refreshTourSteps,
      finishCurrentTour,
    }),
    [persistence.state.completedTours, appTour, handleStartManualTour, handleStartSequence, sequence, refreshTourSteps, finishCurrentTour]
  );

  return (
    <TourContext.Provider value={contextValue}>
      {children}

      <TourWelcomeModal isOpen={showWelcomeModal} onStart={handleStartTour} onSkip={handleSkipTour} />

      <Joyride
        key={joyrideKey}
        steps={activeSteps}
        run={run}
        continuous
        showProgress
        showSkipButton
        disableScrolling={false}
        spotlightClicks
        disableCloseOnEsc={false}
        callback={wrappedCallback}
        tooltipComponent={CustomTooltip}
        floaterProps={{
          placement: 'bottom',
          disableAnimation: false,
          hideArrow: true,
          offset: 35,
        }}
        styles={{
          options: {
            arrowColor: '#1F2937',
            backgroundColor: '#1F2937',
            overlayColor: 'rgba(0, 0, 0, 0.5)',
            primaryColor: '#00992B',
            textColor: '#FFFFFF',
            zIndex: 10000,
          },
          beacon: {
            cursor: 'pointer',
          },
          beaconInner: {
            backgroundColor: '#00C853',
          },
          beaconOuter: {
            backgroundColor: 'rgba(0, 200, 83, 0.2)',
            border: '2px solid #00992B',
          },
        }}
      />
    </TourContext.Provider>
  );
};
